import React, { useEffect, useRef, useState, useCallback } from "react";
import PropTypes from "prop-types";
import { FormGroup, Input, InputGroup, InputGroupText } from "reactstrap";
import "bootstrap/dist/css/bootstrap.min.css";

import HotelIcon from "../../assets/img/hotel.svg?react";
import SearchIcon from "../../assets/img/search.svg?react";

import { formatPropertyLabel } from "../../utils/propertyLabel";

const PropertySelect = ({
  options = [],
  totalCount = 0,
  loading = false,
  searchText = "",
  onSearchTextChange,
  disabled,
  onSelectChange,
  selectedProperty,
  formClassName,
}) => {
  const [selectedValue, setSelectedValue] = useState(
    selectedProperty?.propertyId
  );

  useEffect(() => {
    setSelectedValue(selectedProperty?.propertyId);
  }, [selectedProperty]);

  const isHeaderMode = formClassName === "header-property-select";
  // In header mode the trigger is disabled via prop (e.g. until a property is
  // selected); otherwise fall back to "no options" for the native select.
  const isDisabled = disabled === undefined ? options.length === 0 : disabled;

  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const wrapperRef = useRef(null);
  const listRef = useRef(null);
  const buttonRef = useRef(null);
  const searchInputRef = useRef(null);

  // Label reflects the selected property directly — it may not be present in
  // the current server-side search results. The CRS code is appended so
  // multi-instance hotels (offline vs CRS) are distinguishable.
  const labelForValue =
    formatPropertyLabel(selectedProperty) || "Choose property";

  const handleOpen = useCallback(() => {
    if (isDisabled) return;
    setOpen(true);
    // Set active index to currently selected item
    const idx = options.findIndex(
      (o) => o.propertyId === Number(selectedValue)
    );
    setActiveIndex(Math.max(idx, 0));
    // Focus search input after opening
    setTimeout(() => searchInputRef.current?.focus(), 0);
  }, [isDisabled, options, selectedValue]);

  const resetSearch = useCallback(() => {
    if (onSearchTextChange) onSearchTextChange("");
  }, [onSearchTextChange]);

  const handleClose = useCallback(() => {
    setOpen(false);
    setActiveIndex(-1);
    resetSearch();
    buttonRef.current?.focus();
  }, [resetSearch]);

  const handleSelectItem = useCallback(
    (item) => {
      if (!item) return;
      setSelectedValue(item.propertyId);
      if (onSelectChange) onSelectChange(item);
      resetSearch();
      setOpen(false);
      buttonRef.current?.focus();
    },
    [onSelectChange, resetSearch]
  );

  // Click outside to close
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  // Keyboard interactions
  const onTriggerKeyDown = (e) => {
    if (isDisabled) return;
    switch (e.key) {
      case "Enter":
      case " ":
      case "ArrowDown":
      case "ArrowUp":
        e.preventDefault();
        handleOpen();
        // focus list for SRs
        setTimeout(() => listRef.current?.focus(), 0);
        break;
      default:
        break;
    }
  };

  const onListKeyDown = (e) => {
    if (!open) return;
    if (e.key === "Escape") {
      e.preventDefault();
      handleClose();
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, options.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const item = options[activeIndex];
      handleSelectItem(item);
    }
  };

  const onItemClick = (item, index) => {
    setActiveIndex(index);
    handleSelectItem(item);
  };

  if (isHeaderMode) {
    return (
      <div
        className={["header-property-select", isDisabled ? "is-disabled" : ""]
          .filter(Boolean)
          .join(" ")}
        ref={wrapperRef}
      >
        {/* Trigger */}
        <button
          type="button"
          className="propselect__trigger"
          onClick={() => (open ? handleClose() : handleOpen())}
          onKeyDown={onTriggerKeyDown}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls="propselect-listbox"
          data-open={open ? "true" : "false"}
          disabled={isDisabled}
          ref={buttonRef}
          title={isDisabled ? "No properties" : labelForValue}
        >
          <span className="propselect__icon" aria-hidden="true">
            <HotelIcon />
          </span>
          {/* data-heap-id: selector contract for the Heap Pageview Snapshot
              that captures the displayed property name (property_name) —
              keep stable even if styling classes are renamed. */}
          <span
            className="propselect__label"
            data-heap-id="header-property-name"
          >
            {labelForValue}
          </span>
          <span className="propselect__chevron" aria-hidden="true">
            {/* inline chevron */}
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path
                d="M6 8l4 4 4-4"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
        </button>

        {/* Popover list
            Custom listbox implementation required for integrated search functionality
            and keyboard navigation which cannot be achieved with native <select>.
            ARIA attributes ensure accessibility compliance. */}
        {open && (
          <div
            id="propselect-listbox"
            className="propselect__menu"
            role="listbox"
            aria-label="Property selection list"
            aria-multiselectable="false"
            tabIndex={-1}
            onKeyDown={onListKeyDown}
            ref={listRef}
          >
            {/* Search input */}
            <div className="propselect__menu__search">
              <InputGroup size="sm">
                <InputGroupText>
                  <SearchIcon />
                </InputGroupText>
                <Input
                  innerRef={searchInputRef}
                  type="text"
                  placeholder="Search"
                  value={searchText}
                  onChange={(e) => {
                    if (onSearchTextChange) onSearchTextChange(e.target.value);
                    setActiveIndex(0);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Escape") {
                      e.preventDefault();
                      handleClose();
                    } else if (e.key === "ArrowDown" || e.key === "ArrowUp") {
                      // Let the list handle arrow keys
                      onListKeyDown(e);
                    } else if (e.key === "Enter") {
                      e.preventDefault();
                      const item = options[activeIndex];
                      handleSelectItem(item);
                    }
                  }}
                />
              </InputGroup>
            </div>

            <div className="propselect__menu__count">
              Total Properties: {totalCount}
            </div>

            <ul className="propselect__menu__list">
              {loading && (
                <li className="propselect__menu__row">
                  <div className="propselect__menu__no-results">Searching…</div>
                </li>
              )}

              {!loading &&
                options.length > 0 &&
                options.map((item, idx) => {
                  const selected = Number(selectedValue) === item.propertyId;
                  const active = idx === activeIndex;
                  return (
                    <li key={item.propertyId} className="propselect__menu__row">
                      <button
                        type="button"
                        className={[
                          "propselect__menu__item",
                          selected ? "is-selected" : "",
                          active ? "is-active" : "",
                        ]
                          .filter(Boolean)
                          .join(" ")}
                        role="option"
                        aria-selected={selected}
                        onMouseEnter={() => setActiveIndex(idx)}
                        onClick={() => onItemClick(item, idx)}
                        title={formatPropertyLabel(item)}
                      >
                        {formatPropertyLabel(item)}
                      </button>
                    </li>
                  );
                })}

              {!loading && options.length === 0 && (
                <li className="propselect__menu__row">
                  <div className="propselect__menu__no-results">
                    No properties found
                  </div>
                </li>
              )}
            </ul>
          </div>
        )}
      </div>
    );
  }

  // ========================================================================
  // Default (original) mode – native <select>
  // ========================================================================
  const handleSelectChange = (event) => {
    setSelectedValue(event.target.value);
    const selectedObject = options.find(
      (option) => option.propertyId === Number(event.target.value)
    );
    if (selectedObject && onSelectChange) onSelectChange(selectedObject);
  };

  return (
    <>
      {options.length > 0 ? (
        <FormGroup className={formClassName}>
          <Input
            type="select"
            name="select"
            id="propertySelect"
            style={{ border: "none" }}
            value={selectedValue}
            onChange={handleSelectChange}
          >
            {options?.map((option) => (
              <option key={option.propertyId} value={option.propertyId}>
                {formatPropertyLabel(option)}
              </option>
            ))}
          </Input>
        </FormGroup>
      ) : (
        <span className={`${formClassName} no-prop-span`}>No properties</span>
      )}
    </>
  );
};

PropertySelect.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      propertyId: PropTypes.number.isRequired,
      propertyName: PropTypes.string.isRequired,
      propertyCode: PropTypes.string,
    })
  ),
  totalCount: PropTypes.number,
  loading: PropTypes.bool,
  searchText: PropTypes.string,
  onSearchTextChange: PropTypes.func,
  disabled: PropTypes.bool,
  onSelectChange: PropTypes.func,
  selectedProperty: PropTypes.shape({
    propertyId: PropTypes.number,
    propertyName: PropTypes.string,
    propertyCode: PropTypes.string,
  }),
  formClassName: PropTypes.string,
};

export default PropertySelect;
